import { SDUIHeaderRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Header/SDUIHeaderRenderer";
import { NLSSuggestionsSection } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/SearchDropdown/NLSSuggestionsSection";
import { SDUISimpleActionBannerRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/SearchDropdown/SDUISimpleActionBannerRenderer";
import { SDUISearchSuggestionsRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/SearchDropdown/SearchSuggestions/SDUISearchSuggestionsRenderer";
import { SDUIRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SDUIRenderer/SDUIRenderer";
import { SDUILayoutSectionRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/LayoutSection/SDUILayoutSectionRenderer";
import {
  ComponentRenderMap,
  SectionRenderMap,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/types";
import { ISuggestionEntry } from "@faire/deprecated-retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Interfaces";
import { useSearchSuggestionsStartTimingForQuery } from "@faire/deprecated-retailer-visitor-shared/features/SearchSuggestions-v2/DataFetching/__internal__/useSearchSuggestionsStartTimingForQuery";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { useSettingEnableNLSSearch } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableNLSSearch";
import { useQuery as useSearchSuggestionsEmptyStateQuery } from "@faire/web-api--source/endpoints/www/api/v3/layout/search/empty-page-suggestions/retailerToken/get-hooks";
import { trackSearchSuggestionClick } from "@faire/web-api--source/events/search/click/suggestion";
import { trackSearchSearchSuggestionLoadedView } from "@faire/web-api--source/events/search/view/searchSuggestionLoaded";
import { TimingPair } from "@faire/web/common/timings/TimingMarker";
import { useCallback, useEffect, useRef } from "react";

const componentRenderers: ComponentRenderMap = {
  search_suggestions_component: {
    active: SDUISearchSuggestionsRenderer,
  },
  simple_action_banner_component: {
    active: SDUISimpleActionBannerRenderer,
  },
  header_component: {
    active: SDUIHeaderRenderer,
  },
};
const sectionRenderers: SectionRenderMap = {
  layout_section: SDUILayoutSectionRenderer,
};

export const SDUISearchDropdownEmptyState = ({
  submit,
}: {
  submit: (query: string, submittedUrl?: string, navigate?: boolean) => void;
}) => {
  const retailerToken = getRetailer()?.token ?? "";
  const { data } = useSearchSuggestionsEmptyStateQuery(retailerToken);
  const startTimingMarker = useSearchSuggestionsStartTimingForQuery("");

  const shouldShowNLSSearchSuggestions = useSettingEnableNLSSearch();

  const prevRequestId = useRef<string | undefined>(undefined);
  const requestId = data?.view_layout?.logging_data?.request_id;

  useEffect(() => {
    if (prevRequestId.current !== requestId && startTimingMarker) {
      const timingPair: TimingPair = {
        start: startTimingMarker,
      };
      trackSearchSearchSuggestionLoadedView(timingPair);
    }
    if (prevRequestId.current !== requestId) {
      prevRequestId.current = requestId;
    }
  }, [startTimingMarker, requestId]);

  const onClickSuggestion = useCallback(
    (
      suggestion: ISuggestionEntry,
      query: string,
      suggestionIndex: number,
      sectionIndex: number,
      containerName: string
    ) => {
      // Maintain the same token format as the ResultsDropdown until migrated to SDUI
      const token = `${query}|${suggestion.label}|${suggestionIndex}`;
      trackSearchSuggestionClick(
        `${containerName}|${sectionIndex}`,
        requestId ?? "",
        token
      );
    },
    []
  );

  if (!data?.view_layout) {
    return null;
  }

  const hasNLSSuggestions =
    data?.suggested_natural_language_search_queries &&
    data?.suggested_natural_language_search_queries.length > 0;

  return (
    <div data-test-id="SDUI-search-dropdown-empty-state" className="pb-4">
      {shouldShowNLSSearchSuggestions && hasNLSSuggestions ? (
        <NLSSuggestionsSection
          suggestions={data?.suggested_natural_language_search_queries}
          submit={submit}
          onClickSuggestion={onClickSuggestion}
          sectionIndex={0}
          query=""
        />
      ) : null}
      <SDUIRenderer
        componentRenderers={componentRenderers}
        sectionRenderers={sectionRenderers}
        viewLayout={data.view_layout}
      />
    </div>
  );
};
